import React, { useState } from "react";
import { Grid, Paper, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CcsStatusUpdateSection from "../../prescription_lifecycle/components/prescription/CcsStatusUpdateSection";
import { IFollowUpIssueInput } from "../../prescription_lifecycle/components/prescription/followUp";
import { COLORS } from "../styles/theme";
import { INewRxStepHandoff } from "../types/PrescriptionIngestionTypes";
import { usePrescriptionLookup } from "../hooks/usePrescriptionLookup";
import FollowUpResultCard from "./FollowUpResultCard";
import PrescriptionLookupStatus from "./PrescriptionLookupStatus";

export type CcsReviewMode = "approve" | "followUp" | "either";

export interface ICcsReviewResult {
  prescriptionId: string;
  issues?: IFollowUpIssueInput[];
}

interface IProps {
  mode: CcsReviewMode;
  title: string;
  readyHint: string;
  handoff?: INewRxStepHandoff;
  approvedTitle?: string;
  routedTitle?: string;
  result?: ICcsReviewResult | null;
  onApproved?: (response: any, prescriptionId: string) => void;
  onRouted?: (prescriptionId: string, issues: IFollowUpIssueInput[]) => void;
}

/** Looks up one prescription, then approves it or routes it to CLIVE follow-up, and shows the result. */
const CcsReviewPanel: React.FC<IProps> = ({
  mode,
  title,
  readyHint,
  handoff,
  approvedTitle = title,
  routedTitle = title,
  result,
  onApproved,
  onRouted,
}) => {
  const [reviewed, setReviewed] = useState<ICcsReviewResult | null>(result ?? null);
  const lookup = usePrescriptionLookup(reviewed ? undefined : handoff);

  if (reviewed?.issues) {
    return <FollowUpResultCard title={routedTitle} prescriptionId={reviewed.prescriptionId} issues={reviewed.issues} />;
  }

  if (reviewed) {
    return (
      <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
        <Paper style={{ padding: 24 }}>
          <Typography variant="h6" gutterBottom>
            {approvedTitle}
          </Typography>
          <Alert severity="success">Prescription {reviewed.prescriptionId} approved.</Alert>
        </Paper>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} style={{ width: "100%", maxWidth: 1200 }}>
      <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <div style={{ marginBottom: 16 }}>
          <Alert severity="warning">{readyHint}</Alert>
        </div>

        <PrescriptionLookupStatus lookup={lookup} drugDescription={handoff?.drugDescription} />

        <CcsStatusUpdateSection
          mode={mode}
          requirePrescriptionId
          initialPersonId={handoff?.personId}
          initialPrescriptionId={lookup.prescriptionId}
          submitColor={COLORS.orange}
          onSuccess={(response, prescriptionId) => {
            setReviewed({ prescriptionId });
            onApproved?.(response, prescriptionId);
          }}
          onFollowUp={(_response, prescriptionId, issues) => {
            setReviewed({ prescriptionId, issues });
            onRouted?.(prescriptionId, issues);
          }}
        />
      </Paper>
    </Grid>
  );
};

export default CcsReviewPanel;
